import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PhCheckCircle, PhXCircle, PhGraduationCap } from 'ember-phosphor-icons';
import { formatAsDollars } from '../../../../../utils/format.ts';
import safeFormatDate from '../../../../../helpers/safe-format-date.ts';
import { Section } from 'tio-ui/components/layout';
import Chip from 'tio-ui/components/chip';
import Button from 'tio-ui/components/button';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { AiRecommendationExtension as AiRecommendationModel } from 'tio-data/data/resources/ai-recommendation.ext';
import RecommendationCard from './recommendation-card.gts';
import PolicyDetailModal from './policy-detail-modal.gts';
import PromptDetailModal from './prompt-detail-modal.gts';
import type { DeferReason } from './recommendation-card.gts';

export interface ApplicationAnalysisViewSignature {
  Args: {
    recommendation: AiRecommendationModel;
    application: TasApplicationModel;
  };
}

interface ChargeItem {
  amount: number;
  detail: string;
  date?: string;
  provided_reimbursement_documents: boolean;
  provided_reimbursement_documents_for_associated_course: boolean;
  associated_course_name: string;
  follows_all_policies: boolean;
  is_approved_for_reimbursement: boolean;
  saw_proof_of_payment: boolean;
  proof_of_payment_file_name?: string;
}

interface CreditItem {
  amount: number;
  detail: string;
  proof_of_credit_file_name?: string;
}

interface NameMatch {
  is_name_match: boolean;
  reason: string;
}

interface CourseReimbursementChecklist {
  course_name: string;
  period: string;
  begin_date: string;
  end_date: string;
  document_submission_deadline_date: string;
  course_completion_document_name: string;
  completion_document_submission_date: string;
  completion_document_submitted_within_deadline: boolean;
  course_payment_invoice_document_name: string;
  course_payment_invoice_submission_date: string;
  course_payment_document_submitted_within_deadline: boolean;
  number_of_credits: number;
  grade: string;
  applicant_satisfied_all_requirements_for_reimbursement: boolean;
  reason: string;
}

interface EmployeeEligibility {
  is_eligible: boolean;
  reason: string;
}

interface Proration {
  total_credits_in_application: number;
  total_credits_attempted: number;
  total_credits_passed: number;
}

interface LastNamePresent {
  is_last_name_present: boolean;
  reason: string;
}

interface ReasoningStructure {
  charges?: ChargeItem[];
  credits?: CreditItem[];
  name_match?: NameMatch;
  proration?: Proration;
  last_name_present_in_all_receipts?: LastNamePresent;
  course_reimbursement_checklist?: CourseReimbursementChecklist[];
  employee_eligibility?: EmployeeEligibility;
}

interface RecommendationSummary {
  status: string;
  approved_amount: number;
  available_funds: number;
  requested_amount: number;
  tas_application_id?: string;
  calculation_details?: string;
}

function normalizeWhitespace(text: string) {
  return text.replace(/\s+/g, ' ').trim();
}

interface ResponseJsonStructure {
  reasoning?: ReasoningStructure;
  recommendation_summary?: RecommendationSummary;
  // present on defer_to_human/failure rows; failure rows may carry ONLY
  // { error, defer_reason } with no recommendation_summary at all
  defer_reason?: DeferReason;
  error?: string;
}

class ApplicationAnalysisView extends Component<ApplicationAnalysisViewSignature> {
  @tracked showPolicyModal = false;
  @tracked showPromptModal = false;

  get responseData(): ResponseJsonStructure {
    return (this.args.recommendation.responseJson as ResponseJsonStructure) || {};
  }

  get recommendationSummary(): RecommendationSummary | undefined {
    return this.responseData.recommendation_summary;
  }

  get reasoning(): ReasoningStructure {
    return this.responseData.reasoning || {};
  }

  get recommendation(): string {
    return (
      this.recommendationSummary?.status || this.args.recommendation.recommendedTo || 'UNKNOWN'
    );
  }

  get charges(): ChargeItem[] {
    return this.reasoning.charges || [];
  }

  get credits(): CreditItem[] {
    return this.reasoning.credits || [];
  }

  get nameMatch(): NameMatch | undefined {
    return this.reasoning.name_match;
  }

  get courseReimbursementChecklist(): CourseReimbursementChecklist[] {
    return this.reasoning.course_reimbursement_checklist || [];
  }

  get proration(): Proration | undefined {
    return this.reasoning.proration;
  }

  get lastNamePresent(): LastNamePresent | undefined {
    return this.reasoning.last_name_present_in_all_receipts;
  }

  get employeeEligibility(): EmployeeEligibility | undefined {
    return this.reasoning.employee_eligibility;
  }

  get calculationDetails(): string | undefined {
    return this.recommendationSummary?.calculation_details;
  }

  get deferReason() {
    return this.responseData.defer_reason;
  }

  get approvedAmount() {
    return this.recommendationSummary?.approved_amount;
  }

  get requestedAmount() {
    return this.recommendationSummary?.requested_amount;
  }

  get showRecommendationCard() {
    return !!(this.recommendationSummary || this.deferReason);
  }

  get analysisCreatedAt() {
    return this.args.recommendation.createdAt;
  }

  get analysisCount() {
    return this.args.application.aiRecommendations?.length ?? 0;
  }

  /**
   * Returns stored recommendation context identity only for successful builds; absent historical
   * fields stay absent.
   */
  #contextVersion(key: string) {
    const version = this.#context()?.[key];
    return typeof version === 'string' && version.length > 0 ? version : undefined;
  }

  #context() {
    const context = this.args.recommendation.contextForRecommendation;
    return !context || context['is_error'] ? undefined : context;
  }

  get recommendationContext() {
    return this.#context();
  }

  /** Recorded prompt identity, including in-code pins on pre-store recommendations. */
  get promptVersion() {
    return this.#contextVersion('prompt_version');
  }

  /**
   * Stored prompts use db:<set_key>:v<n>; parse the trailing version because scoped keys contain
   * separators. Pre-RFC-094 pins use dated build numbers. Unknown shapes display verbatim without
   * claiming an origin.
   */
  get promptVersionLabel() {
    const version = this.promptVersion;
    if (!version) return undefined;

    const stored = /^db:(.+):v(\d+)$/.exec(version);
    if (stored) return `Prompt ${stored[1]} v${stored[2]} (prompt store)`;
    if (/^\d{4}\.\d{2}\.\d{2}\.\d+$/.test(version)) return `Prompt ${version} (in code)`;

    return `Prompt ${version}`;
  }

  /**
   * Only stored prompt versions are retrievable. In-code pins stay inert because current defaults
   * cannot recover the historical bytes.
   */
  get promptStoreIdentity() {
    const version = this.promptVersion;
    return version && /^db:(.+):v(\d+)$/.test(version) ? version : undefined;
  }

  openPromptModal = () => {
    this.showPromptModal = true;
  };

  closePromptModal = () => {
    this.showPromptModal = false;
  };

  /**
   * Recorded partner-policy revision (RFC 095); older rows omit the line because it cannot be
   * reconstructed.
   */
  get policyVersion() {
    return this.#contextVersion('policy_version');
  }

  /**
   * Display the trailing version from <company_id>-partner_policy-v<n>; unknown formats remain
   * verbatim.
   */
  get policyVersionLabel() {
    const version = this.policyVersion;
    if (!version) return undefined;

    const stamped = /-partner_policy-v(\d+)$/.exec(version);
    return stamped ? `Policy v${stamped[1]}` : `Policy ${version}`;
  }

  /**
   * Opens the policy snapshot used by the recommendation, never the current policy. A version
   * without stored content keeps an inert caption.
   */
  get policyContent() {
    const policy = this.#context()?.['reimbursement_policy'];
    const isPopulated =
      typeof policy === 'object' &&
      policy !== null &&
      !Array.isArray(policy) &&
      Object.keys(policy).length > 0;

    return isPopulated ? policy : undefined;
  }

  openPolicyModal = () => {
    this.showPolicyModal = true;
  };

  closePolicyModal = () => {
    this.showPolicyModal = false;
  };

  /**
   * Read the sent note from the transition log: the automated decision may have used an older
   * analysis than the one displayed.
   */
  get sentMessage() {
    const transitionLog = this.args.application.transitionLog || [];
    for (let i = transitionLog.length - 1; i >= 0; i--) {
      const transition = transitionLog[i];
      const reason = transition?.reason;
      const note = reason?.note;
      if (!transition || !reason || !note) continue;
      // logs predating the automated flag carry no marker either way — a
      // noted transition without a human actor (`by`) is system-authored
      const isAutomated = reason.automated ?? !reason.by;
      if (isAutomated) {
        return { note, timestamp: transition.timestamp };
      }
    }
    return undefined;
  }

  get showSentMessage() {
    const sent = this.sentMessage;
    if (!sent) return false;
    // when identical, the card's calculation details already ARE the sent text;
    // whitespace-insensitive so multi-line formatting differences don't count
    return normalizeWhitespace(sent.note) !== normalizeWhitespace(this.calculationDetails ?? '');
  }

  get sentMessageHint() {
    const sentAt = new Date(this.sentMessage?.timestamp ?? '').getTime();
    const analyzedAt = new Date(this.analysisCreatedAt ?? '').getTime();
    if (Number.isFinite(sentAt) && Number.isFinite(analyzedAt) && analyzedAt > sentAt) {
      return 'The analysis above was generated after this message was sent — documents or context may have changed.';
    }
    return 'This message was sent based on a different analysis than the one shown above.';
  }

  <template>
    <div class="p-6 space-y-6">
      <div>
        <h3 class="tio-text-base-semibold mb-4 tio-fg-primary">AI Recommendation</h3>
        {{#if this.showRecommendationCard}}
          <RecommendationCard
            @recommendation={{this.recommendation}}
            @calculationDetails={{this.calculationDetails}}
            @approvedAmount={{this.approvedAmount}}
            @requestedAmount={{this.requestedAmount}}
            @deferReason={{this.deferReason}}
          />
          {{#if this.analysisCreatedAt}}
            <p class="tio-text-caption mt-2" data-test-ai-analysis-date>
              Analysis generated
              {{safeFormatDate
                this.analysisCreatedAt
                month="short"
                day="2-digit"
                year="numeric"
                hour="numeric"
                minute="2-digit"
                hour12=true
                timeZoneName="short"
              }}
              {{#if (gt this.analysisCount 1)}}({{this.analysisCount}}
                analyses for this application){{/if}}
            </p>
          {{/if}}
          {{#if this.promptVersionLabel}}
            {{#if this.promptStoreIdentity}}
              <Button
                @appearance="minimal"
                @size="sm"
                @testHook="ai-analysis__prompt-version"
                class="mt-1"
                title={{this.promptVersion}}
                data-test-ai-prompt-version
                {{on "click" this.openPromptModal}}
              >
                {{this.promptVersionLabel}}
              </Button>
            {{else}}
              <p
                class="tio-text-caption mt-1"
                title={{this.promptVersion}}
                data-test-ai-prompt-version
              >
                {{this.promptVersionLabel}}
              </p>
            {{/if}}
          {{/if}}
          {{#if this.policyVersionLabel}}
            {{#if this.policyContent}}
              <Button
                @appearance="minimal"
                @size="sm"
                @testHook="ai-analysis__policy-version"
                class="mt-1"
                title={{this.policyVersion}}
                data-test-ai-policy-version
                {{on "click" this.openPolicyModal}}
              >
                {{this.policyVersionLabel}}
              </Button>
            {{else}}
              <p
                class="tio-text-caption mt-1"
                title={{this.policyVersion}}
                data-test-ai-policy-version
              >
                {{this.policyVersionLabel}}
              </p>
            {{/if}}
          {{/if}}
        {{/if}}
      </div>

      {{#if this.promptStoreIdentity}}
        <PromptDetailModal
          @isOpen={{this.showPromptModal}}
          @onClose={{this.closePromptModal}}
          @identity={{this.promptStoreIdentity}}
          @versionLabel={{this.promptVersionLabel}}
          @context={{this.recommendationContext}}
          @analysisCreatedAt={{this.analysisCreatedAt}}
        />
      {{/if}}

      {{#if this.policyContent}}
        <PolicyDetailModal
          @isOpen={{this.showPolicyModal}}
          @onClose={{this.closePolicyModal}}
          @policy={{this.policyContent}}
          @versionLabel={{this.policyVersionLabel}}
          @rawVersion={{this.policyVersion}}
        />
      {{/if}}

      {{! What the automated reviewer actually sent — may come from an older
          analysis than the one displayed above }}
      {{#if this.showSentMessage}}
        {{#let this.sentMessage as |sentMessage|}}
          {{#if sentMessage}}
            <Section>
              <:header>Message Sent to Employee</:header>
              <:body>
                <div data-test-ai-sent-message>
                  <p class="tio-text-caption mb-1" data-test-ai-sent-message-date>Sent
                    {{safeFormatDate
                      sentMessage.timestamp
                      month="short"
                      day="2-digit"
                      year="numeric"
                      hour="numeric"
                      minute="2-digit"
                      hour12=true
                      timeZoneName="short"
                    }}</p>
                  <p class="text-sm tio-fg-secondary leading-relaxed">{{sentMessage.note}}</p>
                  <p class="tio-text-caption mt-2" data-test-ai-sent-message-hint>
                    {{this.sentMessageHint}}
                  </p>
                </div>
              </:body>
            </Section>
          {{/if}}
        {{/let}}
      {{/if}}

      {{#if this.employeeEligibility}}
        <Section>
          <:header>Employee Eligibility</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.employeeEligibility.is_eligible}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Eligible</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="danger" @size="sm">Not Eligible</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.employeeEligibility.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.courseReimbursementChecklist.length}}
        <Section>
          <:header>Course Reimbursement Checklist</:header>
          <:body>
            <div class="space-y-4">
              {{#each this.courseReimbursementChecklist as |course|}}
                <div class="tio-row-top p-3 tio-surface-sunken">
                  {{#if course.applicant_satisfied_all_requirements_for_reimbursement}}
                    <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="tio-row-tight mb-1">
                      <PhGraduationCap @size="16" class="tio-fg-muted" />
                      <p class="tio-text-sm-semibold tio-fg-primary">{{course.course_name}}</p>
                    </div>
                    {{#if course.grade}}
                      <p class="tio-text-caption mb-1">Grade:
                        {{course.grade}}</p>
                    {{/if}}
                    {{#if course.period}}
                      <p class="tio-text-caption mb-1">Period:
                        {{course.period}}</p>
                    {{/if}}
                    <p class="tio-text-caption mb-1">Credits:
                      {{course.number_of_credits}}</p>
                    <p class="text-sm tio-fg-secondary mt-2">{{course.reason}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.nameMatch}}
        <Section>
          <:header>Name Verification</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.nameMatch.is_name_match}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Name Verified</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Name Mismatch</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.nameMatch.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.lastNamePresent}}
        <Section>
          <:header>Last Name in Receipts</:header>
          <:body>
            <div class="tio-row-top">
              {{#if this.lastNamePresent.is_last_name_present}}
                <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="success" @size="sm">Verified</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{else}}
                <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                <div class="flex-1">
                  <Chip @intent="warning" @size="sm">Not Found</Chip>
                  <p class="text-sm tio-fg-secondary mt-2">{{this.lastNamePresent.reason}}</p>
                </div>
              {{/if}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.proration}}
        <Section>
          <:header>Credit Proration</:header>
          <:body>
            <div class="space-y-2">
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits in Application</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_in_application}}</span>
              </div>
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits Attempted</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_attempted}}</span>
              </div>
              <div class="tio-flex-between text-sm">
                <span class="tio-fg-secondary">Credits Passed</span>
                <span
                  class="font-semibold tio-fg-primary"
                >{{this.proration.total_credits_passed}}</span>
              </div>
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.charges.length}}
        <Section>
          <:header>Charges Analysis</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.charges as |charge|}}
                <div class="tio-row-top p-3 tio-surface-sunken">
                  {{#if charge.is_approved_for_reimbursement}}
                    <PhCheckCircle @size="20" class="tio-fg-success shrink-0 mt-0.5" />
                  {{else}}
                    <PhXCircle @size="20" class="tio-fg-danger shrink-0 mt-0.5" />
                  {{/if}}
                  <div class="flex-1">
                    <div class="tio-row-between mb-1">
                      <p class="tio-text-sm-semibold tio-fg-primary">{{formatAsDollars
                          charge.amount
                          (hash cents=false)
                        }}</p>
                      {{#if charge.is_approved_for_reimbursement}}
                        <Chip @intent="success" @size="sm">Approved</Chip>
                      {{else}}
                        <Chip @intent="danger" @size="sm">Not Approved</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm tio-fg-secondary mb-2">{{charge.detail}}</p>
                    {{#if charge.saw_proof_of_payment}}
                      <div class="tio-row-extra-tight tio-text-caption">
                        <PhCheckCircle @size="14" class="tio-fg-success" />
                        <span>Proof of payment verified</span>
                        {{#if charge.proof_of_payment_file_name}}
                          <span class="tio-fg-muted">•
                            {{charge.proof_of_payment_file_name}}</span>
                        {{/if}}
                      </div>
                    {{/if}}
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}

      {{#if this.credits.length}}
        <Section>
          <:header>Credits & Discounts</:header>
          <:body>
            <div class="space-y-3">
              {{#each this.credits as |credit|}}
                <div class="tio-row-top p-3 app-status-panel app-status-panel--info rounded-lg">
                  <div class="flex-1">
                    <div class="tio-row-between mb-1">
                      <p class="tio-text-sm-semibold tio-fg-primary">-{{formatAsDollars
                          credit.amount
                          (hash cents=false)
                        }}</p>
                      {{#if credit.proof_of_credit_file_name}}
                        <Chip
                          @intent="primary"
                          @size="sm"
                        >{{credit.proof_of_credit_file_name}}</Chip>
                      {{/if}}
                    </div>
                    <p class="text-sm tio-fg-secondary">{{credit.detail}}</p>
                  </div>
                </div>
              {{/each}}
            </div>
          </:body>
        </Section>
      {{/if}}
    </div>
  </template>
}

export default ApplicationAnalysisView;
